import { RadioGroup as ChakraRadioGroup } from "@chakra-ui/react";
import React, { createContext, useMemo, useState } from "react";
import Box, { type BoxProps } from "../../layout/Box/Box.web";
import Checkbox from "../Checkbox/Checkbox.web";
import Radio from "../Radio/Radio.web";
import Tile, { TileProps } from "../Tile/Tile.web";
import type {
  TileGroupCheckboxProps,
  TileGroupRadioProps,
} from "./TileGroup.props";

type TilesContextType = {
  handleChange?: (tileId: string) => void;
  variant: TileGroupRadioProps["variant"] | TileGroupCheckboxProps["variant"];
  selected: Record<string, boolean>;
};

const TilesContext = createContext<TilesContextType>({
  handleChange: undefined,
  variant: "single",
  selected: {},
});

type TileGroupProps = (TileGroupRadioProps | TileGroupCheckboxProps) & {
  children: React.ReactNode;
  defaultChecked?: Record<string, boolean>;
  selected?: Record<string, boolean>;
} & BoxProps;

function TileGroup({
  children,
  onChange,
  direction = "row",
  variant = "single",
  defaultChecked = {},
  selected: controlledSelected,
  ...props
}: TileGroupProps) {
  const [internalSelected, setInternalSelected] =
    useState<Record<string, boolean>>(defaultChecked);

  // Use controlled value if provided, otherwise use internal state
  const selected = controlledSelected ?? internalSelected;

  const handleChange = (tileId: string) => {
    const newSelected =
      variant === "single"
        ? { [tileId]: true }
        : { ...selected, [tileId]: !selected[tileId] };

    // Only update internal state if uncontrolled
    if (controlledSelected === undefined) {
      setInternalSelected(newSelected);
    }

    // Always call onChange with the new value
    if (variant === "single") {
      (onChange as TileGroupRadioProps["onChange"])?.(
        Object.keys(newSelected)[0]
      );
    } else {
      (onChange as TileGroupCheckboxProps["onChange"])?.(
        Object.keys(newSelected).filter((key) => newSelected[key])
      );
    }
  };

  const validChildren = useMemo(() => {
    return React.Children.map(children, (child) => {
      if (
        React.isValidElement(child) &&
        (child.type === Tile || child.type === Tile.Complex)
      ) {
        // If it's already Tile.Complex, use it directly, otherwise create new Tile.Complex
        const complexChild =
          child.type === Tile.Complex
            ? child
            : React.createElement(Tile.Complex, child.props);

        return React.cloneElement<TileProps>(
          complexChild as React.ReactElement<TileProps>,
          {
            flex: 1,
            ...child.props,
            isSelected: selected[child.props.id],
            onClick: () => handleChange?.(child.props.id),
            leadingElement:
              variant === "single" ? (
                <Box display="flex" width="element.xl" height="element.xl">
                  <Radio
                    tabIndex={-1} // Handled by Tile.Complex
                    value={child.props.id}
                  />
                </Box>
              ) : (
                <Box display="flex" width="element.xl" height="element.xl">
                  <Checkbox
                    tabIndex={-1} // Handled by Tile.Complex
                    isChecked={selected[child.props.id]}
                    name={child.props.id}
                    onChange={() => {
                      handleChange?.(child.props.id);
                    }}
                    css={{
                      ".chakra-checkbox__control": {
                        borderColor: "font.dark",
                        "&:hover": {
                          borderColor: "primary.900",
                        },
                        ...(selected[child.props.id] && {
                          borderColor: "primary.800",
                        }),
                      },
                    }}
                  />
                </Box>
              ),
          }
        );
      }
      return null;
    });
  }, [children, handleChange, selected]);

  // Get the currently selected value for RadioGroup (first key that's true)
  const selectedValue =
    Object.keys(selected).find((key) => selected[key]) ?? "";

  const content = (
    <Box
      display="flex"
      gap="lg"
      flexDirection={direction}
      flexWrap="wrap"
      {...props}
    >
      {validChildren}
    </Box>
  );

  return (
    <TilesContext.Provider value={{ handleChange, variant, selected }}>
      {variant === "single" ? (
        <ChakraRadioGroup.Root
          value={selectedValue}
          onValueChange={(details) => handleChange(details.value)}
        >
          {content}
        </ChakraRadioGroup.Root>
      ) : (
        content
      )}
    </TilesContext.Provider>
  );
}

export default TileGroup;

export type { TileGroupProps };
